import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { trpc } from "@/lib/trpc";
import { AlertTriangle, Check, CreditCard, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  max: "Max",
};

const TIER_PRICES: Record<string, string> = {
  free: "€0/mo",
  pro: "€5/mo",
  max: "€20/mo",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-600 border-green-200",
  past_due: "bg-yellow-500/10 text-yellow-600 border-yellow-200",
  canceled: "bg-red-500/10 text-red-600 border-red-200",
};

function formatLimit(n: number): string {
  if (n === Infinity) return "Unlimited";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function UsageBar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max === Infinity ? 0 : Math.min(100, (value / max) * 100);
  const nearLimit = pct >= 80;

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className={nearLimit ? "text-yellow-600 font-medium" : "text-foreground"}>
          {formatLimit(value)} / {formatLimit(max)}
        </span>
      </div>
      {max !== Infinity && (
        <Progress
          value={pct}
          className={nearLimit ? "[&>div]:bg-yellow-500" : undefined}
        />
      )}
      {max === Infinity && (
        <div className="text-xs text-muted-foreground">Unlimited</div>
      )}
    </div>
  );
}

export default function Billing() {
  const { data: sub, isLoading: subLoading } = trpc.billing.getSubscription.useQuery();
  const { data: usage, isLoading: usageLoading } = trpc.billing.getUsage.useQuery();

  const checkoutMutation = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: ({ url }) => {
      if (url) window.location.href = url;
    },
    onError: (err) => toast.error(err.message),
  });

  const portalMutation = trpc.billing.createPortalSession.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (err) => toast.error(err.message),
  });

  const loading = subLoading || usageLoading;
  const tier = usage?.tier ?? "free";
  const atLimit80 =
    (usage?.maxCalls !== Infinity && usage && usage.apiCalls / usage.maxCalls >= 0.8) ||
    (usage?.maxSecrets !== Infinity && usage && usage.secretCount / usage.maxSecrets >= 0.8);

  const periodEnd = sub && "currentPeriodEnd" in sub && sub.currentPeriodEnd
    ? new Date(sub.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6" /> Billing
          </h1>
          <p className="text-muted-foreground mt-1">Manage your plan and usage.</p>
        </div>

        {/* Warning banner */}
        {atLimit80 && (
          <div className="flex items-start gap-3 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-800 p-4 text-sm text-yellow-800 dark:text-yellow-200">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              You&apos;re approaching a plan limit. Upgrade to avoid disruptions.
            </span>
          </div>
        )}

        {/* Current plan + usage */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                Current Plan
                {sub && "status" in sub && sub.status && (
                  <span className={`text-xs font-normal px-2 py-0.5 rounded-full border capitalize ${STATUS_COLORS[sub.status] ?? ""}`}>
                    {sub.status.replace("_", " ")}
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span className="text-2xl font-bold text-foreground">{TIER_LABELS[tier]}</span>
                    {" "}
                    <span className="text-muted-foreground">· {TIER_PRICES[tier]}</span>
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? null : (
                <>
                  {usage && (
                    <>
                      <UsageBar
                        value={usage.apiCalls}
                        max={usage.maxCalls as number}
                        label="API calls this month"
                      />
                      <UsageBar
                        value={usage.secretCount}
                        max={usage.maxSecrets as number}
                        label="Secrets"
                      />
                    </>
                  )}
                  {periodEnd && (
                    <p className="text-xs text-muted-foreground">Usage resets {periodEnd}</p>
                  )}
                  {tier !== "free" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => portalMutation.mutate()}
                      disabled={portalMutation.isPending}
                    >
                      {portalMutation.isPending && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
                      Manage Subscription
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pricing cards */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Plans</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Free */}
            <Card className={tier === "free" ? "border-primary" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Free</CardTitle>
                  {tier === "free" && <Badge>Current</Badge>}
                </div>
                <CardDescription>
                  <span className="text-xl font-bold text-foreground">€0</span>/mo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 secret</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1,000 API calls/mo</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 1 req/sec</li>
                </ul>
              </CardContent>
            </Card>

            {/* Pro */}
            <Card className={tier === "pro" ? "border-primary" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-1">
                    <Zap className="w-4 h-4 text-yellow-500" /> Pro
                  </CardTitle>
                  {tier === "pro" && <Badge>Current</Badge>}
                </div>
                <CardDescription>
                  <span className="text-xl font-bold text-foreground">€5</span>/mo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 10 secrets</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 100,000 API calls/mo</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 10 req/sec</li>
                </ul>
                {tier !== "pro" && tier !== "max" && (
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={() => checkoutMutation.mutate({ tier: "pro" })}
                    disabled={checkoutMutation.isPending}
                  >
                    {checkoutMutation.isPending && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
                    Upgrade to Pro
                  </Button>
                )}
                {tier === "max" && (
                  <Button variant="outline" size="sm" className="w-full" onClick={() => portalMutation.mutate()} disabled={portalMutation.isPending}>
                    Downgrade
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Max */}
            <Card className={tier === "max" ? "border-primary" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-1">
                    <Zap className="w-4 h-4 text-purple-500" /> Max
                  </CardTitle>
                  {tier === "max" && <Badge>Current</Badge>}
                </div>
                <CardDescription>
                  <span className="text-xl font-bold text-foreground">€20</span>/mo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited secrets</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited API calls</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> 100 req/sec</li>
                </ul>
                {tier !== "max" && (
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={() => checkoutMutation.mutate({ tier: "max" })}
                    disabled={checkoutMutation.isPending}
                  >
                    {checkoutMutation.isPending && <Loader2 className="w-3 h-3 mr-2 animate-spin" />}
                    Upgrade to Max
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
